import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 参数示例:
 * ```typescript
 * {
 *   "input": {
 *     "prompt": "一只可爱的橘猫在草地上追逐蝴蝶"
 *   },
 *   "parameters": {
 *     "size": "1280*720",
 *     "prompt_extend": true,
 *     "watermark": false,
 *     "seed": 12345,
 *     "negative_prompt": "模糊，低质量"
 *   }
 * }
 * ```
 */
export interface AlibabaWanx21T2vPlusParams {
  /**
   * 输入参数，包含提示词信息
   */
  input: {
    /**
     * 文本提示词，描述要生成的视频内容，长度不超过800字符
     */
    prompt: string;
  };
  /**
   * 生成参数（可选）
   */
  parameters?: {
    /**
     * 视频分辨率，必须是支持的720P档位分辨率之一，默认1280*720
     * @default "1280*720"
     * @values 1280*720 | 720*1280 | 960*960 | 1088*832 | 832*1088
     */
    size?: '1280*720' | '720*1280' | '960*960' | '1088*832' | '832*1088';
    /**
     * 视频时长（秒），Wanx2.1 T2V Plus 模型固定为5秒，不可修改（设置为其他值将导致验证失败）
     * @default 5
     * @values 5
     */
    duration?: 5;
    /**
     * 是否启用提示词扩展功能，启用后会自动优化和丰富提示词，默认true
     * @default true
     */
    prompt_extend?: boolean;
    /**
     * 是否在生成的视频上添加水印，默认false
     * @default false
     */
    watermark?: boolean;
    /**
     * 负向提示词，描述不希望出现在视频中的内容，长度不超过500字符
     */
    negative_prompt?: string;
    /**
     * 随机种子，用于确保结果可复现，范围0-2147483647
     * @range 0 - 2147483647
     */
    seed?: number;
  };
}

export interface AlibabaWanx21T2vPlusResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * alibaba_wanx21_t2v_plus
 *
 * Alibaba Wanxiang 2.1 T2V Plus 文本生成视频接口，支持从文本描述生成5秒视频。
 * 
 * Model: wanx2.1-t2v-plus
 * Features:
 * - 支持720P档位多种分辨率
 * - 固定生成5秒视频
 * - 支持提示词扩展功能
 * - 支持水印选项
 * - 支持固定随机种子
 * - 不支持音频输入和音频参数
 *
 * @example
 * ```typescript
 * import { initSeacloud, alibabaWanx21T2vPlus } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await alibabaWanx21T2vPlus(
 *   {
 *     "input": {
 *       "prompt": "一只可爱的橘猫在草地上追逐蝴蝶"
 *     },
 *     "parameters": {
 *       "size": "1280*720",
 *       "prompt_extend": true,
 *       "watermark": false,
 *       "seed": 12345,
 *       "negative_prompt": "模糊，低质量"
 *     }
 *   }
 * );
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 AlibabaWanx21T2vPlusParams 接口定义
 * @returns 生成的资源列表
 */
export async function alibabaWanx21T2vPlus(
  params: AlibabaWanx21T2vPlusParams,
): Promise<AlibabaWanx21T2vPlusResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'alibaba_wanx21_t2v_plus',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: AlibabaWanx21T2vPlusResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
